import { Done } from '@mui/icons-material';
import { Box, FormControl, MenuItem, Button as MuiButton, Select, Stack, TextField, Typography } from '@mui/material';
import { EEntityType } from '@sharedInterfaces/globalEnums';
import { EEmployeeTalkStatus } from '@sharedInterfaces/IEmployeeTalk';
import type IEmployeeTalkTemplate from '@sharedInterfaces/IEmployeeTalkTemplate';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { createEmployeeTalk, updateEmployeeTalk } from '@src/APIs/graphQl/EmployeeTalk';
import { getEmployeeTalkTemplates } from '@src/APIs/graphQl/EmployeeTalkTemplate';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import DateInput from '@src/App/Components/General/FormularElements/formsControls/inputs/DateInput/DateInput';
import EmployeeSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/EmployeeSelect/EmployeeSelect';
import type EmployeeTalk from '@src/Objects/EmployeeTalk';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface EmployeeTalkDialogProps {
    id: string;
    employeeTalk?: EmployeeTalk;
    resolve?: (val: EmployeeTalk | null) => void;
    disableEmployeeAndTemplate?: boolean;
}

/**
 * React component representing an employee talk dialog.
 *
 * @param props - The props for the employee talk dialog.
 * @returns JSX element representing the employee talk dialog.
 */
export function EmployeeTalkDialog({
    id,
    employeeTalk,
    resolve,
    disableEmployeeAndTemplate = false,
}: EmployeeTalkDialogProps) {
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const [saving, setSaving] = useState(false);
    const [title, setTitle] = useState(employeeTalk ? employeeTalk.title : '');
    const [employeeId, setEmployeeId] = useState<number | undefined>(
        employeeTalk ? employeeTalk.employeeId : undefined,
    );
    const allOUs = useSelector((state: AppState) => state.OUs);
    const employeeTitle = useSelector(
        (state: AppState) => state.company.allEmployees.find((e) => e.id === employeeId)?.title,
    );

    const [meetingPartnerId, setMeetingPartnerId] = useState<number | undefined>(
        employeeTalk ? employeeTalk.meetingPartnerId : undefined,
    );
    const [templateId, setTemplateId] = useState<number>(employeeTalk ? employeeTalk.templateId : -1);
    const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
        employeeTalk && employeeTalk.scheduledDate ? new Date(employeeTalk.scheduledDate) : new Date(),
    );
    const [status2, setStatus2] = useState<EEmployeeTalkStatus>(
        employeeTalk ? employeeTalk.status2 : EEmployeeTalkStatus.PLANNED,
    );
    const [templates, setTemplates] = useState<IEmployeeTalkTemplate[]>([]);
    const permissions = useSelector((state: AppState) => state.permissions);
    const canViewTemplates = checkPermissions('EmployeeTalkTemplates', 'Retrieve', permissions);

    // Lade Templates aus der GraphQL-API
    useEffect(() => {
        if (canViewTemplates) {
            getEmployeeTalkTemplates()
                .then((templates) => {
                    if (templates) {
                        setTemplates(templates);
                    }
                })
                .catch((error) => {
                    console.error('Error loading templates:', error);
                    showErrorDialog(error instanceof Error ? error : new Error(String(error)));
                });
        }
    }, [canViewTemplates]);

    // Aktualisiere den Titel automatisch, wenn sich die Template-ID ändert
    useEffect(() => {
        if (templateId && employeeId) {
            const template = templates.find((t) => t.id === templateId);
            const templateName = template ? template.title : templateId;
            setTitle(`${templateName}${templateName && employeeTitle ? ' - ' : ''}${employeeTitle}`);
        }
    }, [templateId, employeeTitle, templates]);

    // Funktion zum Finden des Managers eines Mitarbeiters basierend auf seiner OU
    const findManagerForEmployee = (employeeId: number | undefined): number | undefined => {
        if (!employeeId) return undefined;

        // Finde die OU des Mitarbeiters
        const employeeOU = allOUs.find((ou) => ou.employees.some((emp) => emp === employeeId));

        // Wenn eine OU gefunden wurde, gib die ID des Leaders zurück
        return employeeOU?.leader;
    };

    // Aktualisiere den Manager, wenn sich der Mitarbeiter ändert
    useEffect(() => {
        if (employeeId) {
            const managerId = findManagerForEmployee(employeeId);
            if (managerId) {
                setMeetingPartnerId(managerId);
            }
        }
    }, [employeeId]);

    const handleStatusChange = (value: string) => {
        setStatus2(value as EEmployeeTalkStatus);
    };

    const onSave = async () => {
        setSaving(true);

        if (!employeeId || !meetingPartnerId || !templateId) {
            // Hier könnte eine Fehlerbehandlung erfolgen
            setSaving(false);
            return;
        }

        try {
            let savedEmployeeTalk: EmployeeTalk;

            if (employeeTalk) {
                // Update über GraphQL
                savedEmployeeTalk = await updateEmployeeTalk(employeeTalk.id, {
                    title,
                    employeeId,
                    meetingPartnerId,
                    templateId,
                    scheduledDate,
                    status2,
                });
            } else {
                // Create über GraphQL
                savedEmployeeTalk = await createEmployeeTalk(
                    title,
                    employeeId,
                    meetingPartnerId,
                    templateId,
                    scheduledDate,
                    EEmployeeTalkStatus.PLANNED,
                );
            }

            // Dialog schließen
            if (resolve) {
                resolve(savedEmployeeTalk);
            }
        } catch (error) {
            console.error('Error saving employee talk:', error);
            showErrorDialog(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setSaving(false);
        }
    };

    const onClose = () => {
        if (resolve) resolve(null);
    };

    return (
        <Dialog
            id={id}
            title={employeeTalk ? langStrings.editEmployeeTalk : langStrings.newEmployeeTalk}
            onClose={onClose}
            footer={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <MuiButton
                        variant="contained"
                        startIcon={<Done />}
                        disabled={saving || !employeeId || !meetingPartnerId || !templateId}
                        onClick={onSave}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                        }}
                    >
                        {employeeTalk ? langStrings.oftenUsed.save : langStrings.oftenUsed.create}
                    </MuiButton>
                </Box>
            }
        >
            <Stack spacing={3} sx={{ p: 1 }}>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {langStrings.title}:
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                            },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>

                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {langStrings.oftenUsed.employee}:
                    </Typography>
                    <Box sx={{ mt: 0 }}>
                        <EmployeeSelect
                            value={employeeId}
                            onChange={setEmployeeId}
                            disabled={disableEmployeeAndTemplate}
                        />
                    </Box>
                </Box>

                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {langStrings.manager}:
                    </Typography>
                    <Box sx={{ mt: 0 }}>
                        <EmployeeSelect value={meetingPartnerId} onChange={setMeetingPartnerId} />
                    </Box>
                </Box>

                {canViewTemplates && (
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {langStrings.template}:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <Select
                                    value={templateId || ''}
                                    onChange={(e) => setTemplateId(Number(e.target.value))}
                                    disabled={disableEmployeeAndTemplate}
                                    displayEmpty
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        {langStrings.oftenUsed.pleaseSelect}
                                    </MenuItem>
                                    {templates.map((template) => (
                                        <MenuItem key={template.id} value={template.id}>
                                            {template.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}

                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {langStrings.scheduledDate}:
                    </Typography>
                    <Box sx={{ mt: 0 }}>
                        <DateInput value={scheduledDate} onChange={setScheduledDate} />
                    </Box>
                </Box>

                {employeeTalk && (
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {langStrings.status}:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <Select
                                    value={status2}
                                    onChange={(e) => handleStatusChange(e.target.value as string)}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        },
                                    }}
                                >
                                    {Object.values(EEmployeeTalkStatus).map((statusValue) => (
                                        <MenuItem key={statusValue} value={statusValue}>
                                            {statusValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
            </Stack>
        </Dialog>
    );
}
